import { PaymentMethodType } from '@entities/payment-methods'
import { RegisterDeliveryInformationFormState } from '@features/delivery'
import { RegisterCardFormState } from '@features/payment-methods'
import { openRegisterCardErrorAlertDialog } from '@features/payment/lib/validation'
import { PaymentType } from '@server/schemas'
import { useTranslation } from '@shared/config/i18n'
import { openAlertDialog } from '@shared/ui'
import { match, P } from 'ts-pattern'

type Props = {
  isTermsAgreed: boolean
  requiresTermsAgreement?: boolean
  paymentType: PaymentType
  paymentMethodType: Nullable<PaymentMethodType>
  registerCardFormState?: RegisterCardFormState
  registerDeliveryInformationFormState?: RegisterDeliveryInformationFormState
}

export const usePaymentValidation = ({
  isTermsAgreed,
  requiresTermsAgreement = true,
  paymentType,
  paymentMethodType,
  registerCardFormState,
  registerDeliveryInformationFormState,
}: Props) => {
  const t = useTranslation()

  const validateClientSideRegisterDeliveryInformation = () => {
    return match(registerDeliveryInformationFormState?.errors)
      .returnType<boolean>()
      .with(P.union({ name: { hasError: true, errorMessage: P.any } }), () => {
        openAlertDialog({ title: t('delivery.validation.nameRequired') })
        return false
      })
      .with(P.union({ phone: { hasError: true, errorMessage: P.any } }), () => {
        openAlertDialog({ title: t('delivery.validation.phoneInvalid') })
        return false
      })
      .with(P.union({ zipCode: { hasError: true, errorMessage: P.any } }), () => {
        openAlertDialog({ title: t('delivery.validation.zipCodeRequired') })
        return false
      })
      .with(P.union({ address: { hasError: true, errorMessage: P.any } }), () => {
        openAlertDialog({ title: t('delivery.validation.addressRequired') })
        return false
      })
      .with(P.union({ detailAddress: { hasError: true, errorMessage: P.any } }), () => {
        openAlertDialog({ title: t('delivery.validation.detailAddressRequired') })
        return false
      })
      .otherwise(() => true)
  }
  const validateClientSideRegisterCard = () => {
    return match(registerCardFormState?.errors)
      .returnType<boolean>()
      .with(
        P.union(
          { cardNumber1: { hasError: true, errorMessage: P.any } },
          { cardNumber2: { hasError: true, errorMessage: P.any } },
          { cardNumber3: { hasError: true, errorMessage: P.any } },
          { cardNumber4: { hasError: true, errorMessage: P.any } },
        ),
        () => {
          openAlertDialog({ title: t('paymentMethods.creditCard.validation.cardNumberRequired') })
          return false
        },
      )
      .with(
        P.union(
          { cardExpiryMonth: { hasError: true, errorMessage: P.any } },
          { cardExpiryYear: { hasError: true, errorMessage: P.any } },
        ),
        () => {
          openAlertDialog({ title: t('paymentMethods.creditCard.validation.invalidExpiration') })
          return false
        },
      )
      .with({ cardPassword: { hasError: true, errorMessage: P.any } }, () => {
        openAlertDialog({ title: t('paymentMethods.creditCard.validation.passwordRequired') })
        return false
      })
      .with({ birthDate: { hasError: true, errorMessage: P.any } }, () => {
        openAlertDialog({ title: t('paymentMethods.creditCard.validation.birthDateRequired') })
        return false
      })
      .otherwise(() => true)
  }

  const validatePayment = (): boolean => {
    if (paymentType === 'IPAD') {
      if (!validateClientSideRegisterDeliveryInformation()) {
        return false
      }
    }

    if (!paymentMethodType) {
      openAlertDialog({ title: t('paymentMethods.validation.selectPaymentMethod') })
      return false
    }

    if (paymentMethodType === 'CARD' && (paymentType === 'TRIAL' || paymentType === 'SUBSCRIBE')) {
      if (!validateClientSideRegisterCard()) {
        return false
      }
    }

    if (requiresTermsAgreement && !isTermsAgreed) {
      openAlertDialog({ title: t('paymentMethods.validation.termsRequired') })
      return false
    }

    return true
  }

  return {
    validateClientSideRegisterDeliveryInformation,
    validateClientSideRegisterCard,
    validateServerSideRegisterCard: (error: Error) => openRegisterCardErrorAlertDialog(error, t),
    validatePayment,
  }
}
